import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Download, FileJson, FileSpreadsheet, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

export type RecipeBoxHeaderProps = Readonly<{
  title: string;
  description?: string | null;
  canExport?: boolean;
  onExportJSON?: () => void;
  onExportCSV?: () => void;
  isOwner?: boolean;
  onEditBox?: () => void;
  onDeleteBox?: () => void;
}>;

export const RecipeBoxHeader = ({
  title,
  description,
  canExport = false,
  onExportJSON,
  onExportCSV,
  isOwner = false,
  onEditBox,
  onDeleteBox,
}: RecipeBoxHeaderProps) => {
  const hasDescription = description && description.trim().length > 0;
  const defaultDescription = "Start filling this recipe box with your culinary favorites.";

  return (
    <Collapsible defaultOpen={false} className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-center justify-between p-4 sm:p-6">
        <CollapsibleTrigger asChild className="flex-1 text-left">
          <div className="flex items-center gap-2 flex-1">
            <h1 className="font-headline text-xl sm:text-3xl font-bold text-foreground">{title}</h1>
            {hasDescription && (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 data-[state=open]:rotate-180" />
            )}
          </div>
        </CollapsibleTrigger>
        <div className="flex items-center gap-2 ml-4">
          {canExport && (onExportJSON || onExportCSV) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="outline" size="sm">
                  <Download className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Export All</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onExportJSON && (
                  <DropdownMenuItem onClick={onExportJSON}>
                    <FileJson className="mr-2 h-4 w-4" />
                    Export as JSON
                  </DropdownMenuItem>
                )}
                {onExportCSV && (
                  <DropdownMenuItem onClick={onExportCSV}>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Export as CSV
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isOwner && (onEditBox || onDeleteBox) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Box options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEditBox && (
                  <DropdownMenuItem onClick={onEditBox}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Recipe Box
                  </DropdownMenuItem>
                )}
                {onEditBox && onDeleteBox && <DropdownMenuSeparator />}
                {onDeleteBox && (
                  <DropdownMenuItem onClick={onDeleteBox} className="text-destructive focus:text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Recipe Box
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {hasDescription && (
        <CollapsibleContent className="px-4 pb-4 sm:px-6 sm:pb-6">
          <p className="text-sm sm:text-base text-muted-foreground">
            {description || defaultDescription}
          </p>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
};

